import type { BoardContent, CellSpec } from '../core/types';
import { DEFAULT_GRID, difficultyForBoard } from '../core/config';

// Hand-authored, fact-checked offline boards. These let M0 prove the loop with ZERO live LLM,
// and they are the offline fallback + PvP-fairness baseline later. Every "correct" here is
// genuinely correct and every "decoy" genuinely fails the rule — the trust principle applies to
// the seed pack too. Cell text is kept <= 22 chars (MAX_CELL_CHARS) for phone legibility.

const STANDARD_DESKTOP_PARAMS = [0, 1, 2, 3, 4].map((rung) => difficultyForBoard(rung, { rows: 6, cols: 6 }, undefined, true));

const EXTRA_DECOYS: Record<string, readonly string[]> = {
  'geography::host_cities_2026': ['Baltimore', 'Cleveland', 'Cincinnati', 'Raleigh', 'St. Louis', 'Indianapolis'],
  'art::shades_of_red': ['Cornflower', 'Midnight Blue', 'Ice Blue', 'Prussian Blue', 'Baby Blue', 'Electric Blue', 'Oxford Blue'],
  'science::gemstones': ['Andesite', 'Rhyolite', 'Diorite', 'Siltstone', 'Mudstone', 'Laterite'],
  'nature::chinese_zodiac_animals': ['Giraffe', 'Gorilla', 'Zebra', 'Hippo', 'Rhino', 'Cheetah'],
  'biology::insects_not_bugs': ['Starfish', 'Clam', 'Oyster', 'Squid', 'Octopus', 'Barnacle'],
  'mythology::greek_gods': ['Quirinus', 'Bellona', 'Flora', 'Pomona', 'Vesta', 'Ops'],
  'chemistry::elements_gas_stp': ['Magnesium', 'Platinum', 'Lithium', 'Barium', 'Uranium', 'Titanium'],
  'math::even_numbers': ['39', '41', '43', '45', '47', '49'],
  'geography::us_state_capitals': ['Los Angeles', 'Cleveland', 'Cincinnati', 'St. Louis', 'New Orleans', 'Philadelphia'],
  'math::prime_numbers': ['85', '87', '93', '95', '99', '111'],
  'literature::shakespeare_plays': ['Cherry Orchard', "Long Day's Journey", 'Glass Menagerie', 'Death of a Salesman', 'Waiting for Lefty', 'The Balcony'],
  'biology::mammals': ['Goldfish', 'Trout', 'Parrot', 'Alligator', 'Salamander', 'Clam'],
  'geography::countries_africa': ['Portugal', 'Malaysia', 'Belize', 'Ukraine', 'Sweden', 'Laos'],
  'geography::landlocked_countries': ['Japan', 'Indonesia', 'Canada', 'Mexico', 'Thailand', 'Spain'],
  'geography::island_nations': ['Germany', 'France', 'Poland', 'Egypt', 'Kenya', 'Colombia'],
  'geography::us_coastal_states': ['Minnesota', 'Illinois', 'Indiana', 'Ohio', 'North Dakota', 'Wisconsin'],
  'geography::south_america_capitals': ['Curitiba', 'Porto Alegre', 'Santa Cruz', 'Barquisimeto', 'La Plata', 'Punta Arenas'],
  'geography::european_capitals': ['Nice', 'Turin', 'Cologne', 'Antwerp', 'Bilbao', 'Malmo'],
  'geography::countries_asia': ['Portugal', 'Ireland', 'Norway', 'Belgium', 'Colombia', 'Bolivia'],
  'geography::us_national_parks': ['Golden Gate', 'Alcatraz', 'Liberty Bell', 'Central Park', 'Times Square', 'Hoover Dam'],
  'geography::eurozone_countries': ['Canada', 'Japan', 'Brazil', 'Mexico', 'China', 'India', 'Australia'],
  'science::metallic_elements': ['Water', 'Salt', 'Glass', 'Plastic', 'Wood', 'Rubber'],
  'science::human_bones': ['Orbicularis', 'Temporalis', 'Buccinator', 'Platysma', 'Pronator', 'Supinator'],
  'science::vitamins_not_minerals': ['Nickel', 'Vanadium', 'Strontium', 'Barium', 'Tin', 'Silica'],
  'science::one_letter_element_symbols': ['Titanium', 'Barium', 'Chromium', 'Manganese', 'Gold', 'Silver'],
  'science::viral_diseases': ['Shigellosis', 'MRSA infection', 'Trachoma', 'Brucellosis', 'Listeriosis', 'Campylobacteriosis'],
  'science::human_organs': ['Carpal', 'Tarsal', 'Maxilla', 'Temporal bone', 'Parietal bone', 'Occipital bone'],
  'history::us_presidents': ['Mitt Romney', 'Al Gore', 'John McCain', 'Hillary Clinton', 'Bernie Sanders', 'Ross Perot'],
  'history::thirteen_colonies': ['Minnesota', 'Oregon', 'Nevada', 'Arizona', 'Colorado', 'Nebraska'],
  'history::ancient_civilizations': ['Thailand', 'Vietnam', 'Colombia', 'Spain', 'Norway', 'Poland'],
  'history::british_empire': ['Italy', 'China', 'France', 'Argentina', 'Peru', 'Greece'],
  'history::roman_emperors': ['Plutarch', 'Cleanthes', 'Chrysippus', 'Xenophon', 'Isocrates', 'Sextus Empiricus'],
  'history::fallen_empires': ['Japan', 'Canada', 'Australia', 'Nigeria', 'Sweden', 'Poland'],
  'sports::summer_games_sports': ['Ice Dancing', 'Sled Hockey', 'Snowshoeing', 'Ski Cross', 'Big Air', 'Aerials', 'Ice Climbing'],
  'science::metric_units': ['Rod', 'Chain', 'Hand', 'Nautical Mile', 'Knot', 'Grain'],
  'math::perfect_cubes': ['2', '3', '5', '6', '7', '10', '12', '15', '18'],
  'sports::winter_games_sports': ['Surfing', 'Skateboarding', 'Rugby Sevens', 'Taekwondo', 'Canoe Sprint', 'Water Polo'],
  'sports::soccer_host_nations': ['Czechia', 'Slovakia', 'Slovenia', 'Albania', 'Iceland', 'Wales', 'Lithuania'],
  'sports::ball_sports': ['Taekwondo', 'Diving', 'Gymnastics', 'Ski Jumping', 'Speed Skating', 'Luge', 'Bobsled'],
  'food::coffee_drinks': ['English Breakfast', 'Genmaicha', 'Lapsang', 'Ceylon Tea', 'Nilgiri', 'Keemun', 'Gyokuro'],
  'music::string_instruments': ['English Horn', 'Bass Clarinet', 'Euphonium', 'Ocarina', 'Shawm', 'Zurna', 'Kazoo'],
  'science::cloud_types': ['Dust Devil', 'Heat Wave', 'Cold Front', 'Waterspout', 'Sandstorm', 'Aurora', 'Rime'],
  'math::powers_of_two': ['40', '72', '80', '160', '384', '640', '900'],
  'music::music_genres': ['Pirouette', 'Plie', 'Arabesque', 'Jete', 'Pas de Deux', 'Moonwalk'],
  'biology::marsupials': ['Capybara', 'Bat', 'Fox', 'Wolf', 'Bison', 'Giraffe'],
  'music::percussion_instruments': ['Charango', 'Oud', 'Twelve-string', 'Classical Guitar', 'Electric Guitar', 'Acoustic Guitar'],
  'music::woodwind_instruments': ['Cimbasso', 'Ophicleide', 'Alto Trombone', 'Bass Trumpet', 'Tenor Trombone', 'Contrabass Tuba'],
  'science::natural_fibers': ['Vinyon', 'Saran', 'Elastolefin', 'Vectran', 'Dyneema', 'Spectra'],
  'film::best_picture_winners': ['The Social Network', 'Brokeback Mountain', 'Citizen Kane', 'Taxi Driver', 'Jaws', 'The Revenant'],
  'food::citrus_fruits': ['Juneberry', 'Serviceberry', 'Tayberry', 'Dewberry', 'Blackcurrant', 'Redcurrant'],
  'film::horror_movies': ['Clueless', 'Napoleon Dynamite', 'Airplane!', 'Zoolander', 'The Jerk', 'Office Space'],
  'tech::hand_tools': ['Band Saw', 'Drill Press', 'Tile Saw', 'Power Washer', 'Oscillating Tool', 'Orbital Sander'],
  'food::pasta_shapes': ['Chapati', 'Lavash', 'Biscuit', 'Scone', 'English Muffin', 'Breadstick', 'Kaiser Roll'],
  'biology::amphibians': ['Boa', 'Rattlesnake', 'Monitor', 'Caiman', 'Sea Turtle', 'Viper', 'Anaconda'],
  'geography::deserts': ['Dolomites', 'Karakoram', 'Hindu Kush', 'Sierra Madre', 'Kunlun', 'Alay Range'],
  'tv::sitcoms_not_dramas': ['The Americans', 'The X-Files', 'Twin Peaks', 'The Shield', 'Law & Order', 'True Detective'],
  'sports::baseball_positions': ['Tight End', 'Nose Tackle', 'Hooker', 'Prop', 'Flanker', 'Lock', 'Fly-half'],
  'tv::reality_competition': ['The Mandalorian', 'Severance', 'The Bear', 'Fargo', 'The Handmaid', 'True Detective'],
  'science::constellations': ['Fomalhaut', 'Bellatrix', 'Algol', 'Menkent', 'Mirach', 'Alnilam'],
  'food::fruits_not_vegetables': ['Chard', 'Collard Greens', 'Fennel', 'Artichoke', 'Kohlrabi', 'Celeriac'],
  'food::italian_dishes': ['Huarache', 'Tlacoyo', 'Gordita', 'Ceviche', 'Tinga', 'Esquites'],
  'food::cheeses_not_wines': ['Syrah', 'Viognier', 'Gewurztraminer', 'Barolo', 'Bordeaux', 'Cava'],
  'food::spices_not_herbs': ['Coriander Leaf', 'Fennel Frond', 'Thai Basil', 'Lemon Balm', 'Shiso', 'Epazote', 'Curry Leaf'],
  'food::fast_food_chains': ["Maggiano's", 'Bonefish Grill', 'Yard House', 'Capital Grille', 'Mellow Mushroom', 'Bahama Breeze', 'Fogo de Chao'],
  'food::classic_cocktails': ['Fruit Punch', 'Milkshake', 'Kombucha', 'Grape Juice', 'Limeade', 'Smoothie', 'Mineral Water'],
  'food::desserts_not_savory': ['Ramen', 'Pho', 'Curry', 'Steak', 'Baked Salmon', 'Falafel', "Shepherd's Pie"],
  'food::root_vegetables': ['Frisee', 'Mizuna', 'Butter Lettuce', 'Chinese Cabbage', 'Mustard Spinach', 'Beet Greens', 'Microgreens'],
  'music::tempo_markings': ['Subito Piano', 'Sforzando Piano', 'Forte-piano', 'Crescendo Poco', 'Diminuendo Poco', 'Marcato', 'Tenuto'],
  'gaming::fps_games': ['Dragon Ball FighterZ', 'Under Night', 'Melty Blood', 'Fatal Fury', 'Power Stone', 'Rivals of Aether', "Them's Fightin' Herds"],
  'food::sushi_types': ['Karaage', 'Tonkatsu', 'Nikujaga', 'Curry Rice', 'Kaiseki', 'Sukiyaki', 'Yakisoba'],
  'tech::data_structures': ['Bellman-Ford', 'Floyd-Warshall', 'Topological Sort', 'Linear Search', 'A-Star Search', 'Memoization', 'Divide and Conquer'],
  'gaming::indie_games': ['Mass Effect', 'Resident Evil', 'Uncharted', 'Tomb Raider', 'Elden Ring', 'Diablo IV', 'Starfield'],
  'gaming::fighting_games': ['Midnight Club', 'Sega Rally', 'Assetto Corsa', 'iRacing', 'WRC', 'NASCAR Heat', 'The Crew'],
  'internet::social_media_apps': ['Vimeo', 'Kanopy', 'Fandango at Home', 'Plex', 'Curiosity Stream', 'Shudder', 'Acorn TV'],
  'internet::face_emoji': ['🐕', '🐈', '🐇', '🐓', '🦆', '🦢', '🦜'],
  'science::alloys': ['Vanadium', 'Bismuth', 'Gallium', 'Indium', 'Rhodium', 'Iridium'],
  'internet::ai_chatbots': ['WolframAlpha', 'Mojeek', 'Seznam', 'Swisscows', 'MetaGer', 'Kagi'],
  'internet::video_streaming': ['Last.fm', 'TuneIn', 'Mixcloud', 'LiveOne', 'SiriusXM', 'KKBOX', 'Saavn'],
  'language::romance_languages': ['West Frisian', 'Plautdietsch', 'Pennsylvania Dutch', 'Old English', 'Old Norse', 'Middle Dutch', 'Saterland Frisian'],
  'language::greek_alphabet': ['Latin A', 'Latin B', 'Latin E', 'Latin I', 'Latin O', 'Latin X', 'Latin Y'],
  'language::palindromes': ['random', 'planet', 'window', 'bridge', 'market', 'signal', 'rocket'],
  'language::nato_alphabet': ['Michael', 'Anthony', 'Patricia', 'Jennifer', 'Elizabeth', 'Barbara'],
  'nature::wild_cats': ['Great Dane', 'Akita', 'Maltese', 'Whippet', 'Samoyed', 'Pointer'],
  'nature::snakes_not_lizards': ['Uromastyx', 'Tokay Gecko', 'Water Monitor', 'Caiman Lizard', 'Blue-tongue Skink', 'Leopard Gecko'],
  'nature::flightless_birds': ['Kingfisher', 'Swallow', 'Starling', 'Woodpecker', 'Hummingbird', 'Parakeet'],
  'nature::dinosaurs': ['Saber-tooth Cat', 'Dire Wolf', 'Eurypterid', 'Hallucigenia', 'Helicoprion', 'Arthropleura', 'Cave Bear'],
  'nature::deciduous_trees': ['Norfolk Pine', 'Araucaria', 'Ponderosa Pine', 'Sitka Spruce', 'Grand Fir', 'White Cedar', 'Leyland Cypress'],
  'nature::ocean_mammals': ['Snapper', 'Anchovy', 'Perch', 'Carp', 'Catfish', 'Flounder'],
  'tech::programming_languages': ['HCL', 'RDF', 'EDN', 'Parquet', 'Avro', 'ORC'],
  'tech::apple_products': ['Galaxy S22', 'Galaxy S21', 'Galaxy Fit', 'Galaxy Home', 'Galaxy Chromebook', 'Galaxy Book Pro'],
  'tech::ev_makers': ['Morgan', 'Caterham', 'BAC', 'Ariel', 'Wiesmann', 'Saleen', 'Zenvo'],
  'tech::image_file_formats': ['MKA', 'RA', 'AC3', 'DTS', 'MPC', 'OGA'],
  'tech::ai_companies': ['Flickr', 'Meetup', 'Discord', 'WhatsApp', 'Telegram', 'Substack', 'Medium'],
  'art::impressionist_painters': ['Donatello', 'Brunelleschi', 'Ghiberti', 'Piero della Francesca', 'Fra Filippo Lippi', 'Andrea del Sarto'],
  'art::art_movements': ['Cape Cod', 'Ranch', 'Bungalow', 'Shingle Style', 'Richardsonian', 'Chateauesque', 'Foursquare'],
  'art::paintings_not_sculptures': ['Cloud Gate', 'The Bean', 'Ecstasy of St Teresa', 'Little Dancer', 'Bronze Horseman', 'Maman', 'Spoonbridge'],
  'space::solar_system_moons': ['Vesta', 'Pallas', 'Hygiea', 'Eros', 'Bennu', 'Apophis', 'Hebe'],
  'space::zodiac_constellations': ['Bootes', 'Lupus', 'Vela', 'Carina', 'Puppis', 'Lacerta'],
  'space::nasa_missions': ['Falcon 9 v1.0', 'Falcon 9 v1.1', 'Falcon 9 FT', 'Falcon 9 Block 5', 'Starship HLS', 'Dragon 2', 'Starlink V2'],
  'space::space_agencies': ['Saab', 'Learjet', 'Cirrus', 'Mooney', 'de Havilland', 'BAE Systems'],
  'space::stars_not_planets': ['Pluto', 'Ceres', 'Eris', 'Haumea', 'Makemake', 'Sedna', 'Quaoar'],
  'math::perfect_squares': ['3', '5', '6', '7', '10', '11'],
  'math::polygons_not_curves': ['Annulus', 'Arc', 'Ovoid', 'Wave', 'Spiral', 'Blob', 'Loop'],
  'math::fibonacci_numbers': ['16', '18', '20', '24', '25', '27'],
  'math::multiples_of_three': ['101', '103', '104', '106', '107', '109'],
  'mythology::norse_figures': ['Pan', 'Nike', 'Iris', 'Hebe', 'Nemesis', 'Tyche'],
  'mythology::mythical_creatures': ['Giraffe', 'Hippo', 'Rhino', 'Cheetah', 'Gorilla', 'Panda'],
  'mythology::egyptian_gods': ['Pan', 'Nike', 'Iris', 'Hebe', 'Nemesis', 'Tyche'],
  'business::car_brands': ['GasGas', 'Beta', 'Sherco', 'Rieju', 'Kymco', 'CFMoto', 'Derbi'],
  'business::world_currencies': ['Australia', 'South Africa', 'Argentina', 'Colombia', 'Indonesia', 'Pakistan'],
  'business::luxury_brands': ['Missguided', 'PrettyLittleThing', 'Fashion Union', 'River Island', 'Target', 'Walmart', 'Peacocks'],
  'business::cryptocurrencies': ['Forint', 'Koruna', 'Leu', 'Hryvnia', 'Tenge', 'Ringgit', 'Naira', 'Zloty'],
  'business::airlines_not_hotels': ['Accor', 'Motel 6', 'Travelodge', 'Premier Inn', 'Hampton Inn', 'Scandic'],
};

function labelKey(text: string): string {
  return text.trim().toLowerCase();
}

function requiredSeedDecoys(correctCount: number): number {
  return Math.max(...STANDARD_DESKTOP_PARAMS.map((p) => p.rows * p.cols - p.walls - Math.min(p.targetCorrect, correctCount)));
}

function padSeedDecoys(topicNormalized: string, correct: readonly string[], decoy: readonly string[]): string[] {
  const requiredDecoys = requiredSeedDecoys(correct.length);
  const padded = [...decoy];
  const seen = new Set([...correct, ...decoy].map(labelKey));
  for (const text of EXTRA_DECOYS[topicNormalized] ?? []) {
    const clean = text.trim();
    if (!clean || seen.has(labelKey(clean))) continue;
    padded.push(clean);
    seen.add(labelKey(clean));
    if (padded.length >= requiredDecoys) break;
  }
  // Runtime must never fail the whole offline seed pack at module import. The data validator enforces
  // this decoy-floor fail-closed in CI/local gates; shipped clients keep the authored pool.
  return padded;
}

function board(
  topicRaw: string,
  topicNormalized: string,
  ruleStatement: string,
  correct: string[],
  decoy: string[],
): BoardContent {
  const paddedDecoys = padSeedDecoys(topicNormalized, correct, decoy);
  const cells: CellSpec[] = [
    ...correct.map((text): CellSpec => ({ text, kind: 'correct' })),
    ...paddedDecoys.map((text): CellSpec => ({ text, kind: 'decoy' })),
  ];
  return {
    topicRaw,
    topicNormalized,
    ruleStatement,
    rows: DEFAULT_GRID.rows,
    cols: DEFAULT_GRID.cols,
    cells,
    source: 'seed',
    verified: true,
  };
}

export const SEED_BOARDS: BoardContent[] = [
  board(
    '2026 Tournament Host Cities',
    'geography::host_cities_2026',
    'Eat only 2026 soccer-tournament host cities; leave the rest.',
    ['Atlanta', 'Dallas', 'Houston', 'Kansas City', 'Los Angeles', 'Miami', 'Philadelphia', 'Seattle', 'Toronto', 'Vancouver', 'Mexico City', 'Monterrey'],
    ['Chicago', 'Denver', 'Phoenix', 'Las Vegas', 'San Diego', 'Washington DC', 'Detroit', 'Minneapolis', 'New Orleans', 'Orlando', 'Nashville', 'Charlotte', 'Pittsburgh', 'San Antonio', 'Montreal', 'Calgary', 'Ottawa', 'Tijuana', 'Cancun'],
  ),
  board(
    'Shades of Red',
    'art::shades_of_red',
    'Eat only shades of red; leave shades of blue.',
    ['Crimson', 'Scarlet', 'Maroon', 'Ruby', 'Vermilion', 'Cardinal', 'Burgundy', 'Brick', 'Garnet', 'Cherry', 'Rose'],
    ['Navy', 'Azure', 'Cobalt', 'Cyan', 'Teal', 'Indigo', 'Sapphire', 'Cerulean', 'Turquoise', 'Denim', 'Royal Blue', 'Powder Blue', 'Steel Blue', 'Periwinkle', 'Ultramarine', 'Aqua', 'Slate Blue', 'Sky Blue'],
  ),
  board(
    'Gemstones',
    'science::gemstones',
    'Eat only gemstones; leave common rocks.',
    ['Diamond', 'Ruby', 'Sapphire', 'Emerald', 'Amethyst', 'Topaz', 'Opal', 'Garnet', 'Aquamarine', 'Peridot', 'Turquoise', 'Jade'],
    // #10: 'Obsidian' → 'Dolomite'. Obsidian is arguably a semi-precious gem (used in jewelry), so it was
    // a contestable decoy; Dolomite is an unambiguous common rock/mineral. Count/shape unchanged (19 decoys).
    ['Granite', 'Basalt', 'Limestone', 'Sandstone', 'Marble', 'Slate', 'Shale', 'Dolomite', 'Pumice', 'Quartzite', 'Gneiss', 'Schist', 'Chalk', 'Flint', 'Gabbro', 'Conglomerate', 'Travertine', 'Tuff', 'Breccia'],
  ),
  board(
    'Chinese Zodiac Animals',
    'nature::chinese_zodiac_animals',
    'Eat only Chinese zodiac animals; leave the rest.',
    ['Rat', 'Ox', 'Tiger', 'Rabbit', 'Dragon', 'Snake', 'Horse', 'Goat', 'Monkey', 'Rooster', 'Dog', 'Pig'],
    ['Cat', 'Bear', 'Elephant', 'Lion', 'Wolf', 'Fox', 'Deer', 'Eagle', 'Owl', 'Frog', 'Panda', 'Camel', 'Donkey', 'Duck', 'Turtle', 'Whale', 'Shark', 'Penguin', 'Kangaroo'],
  ),
  board(
    'Insects',
    'biology::insects_not_bugs',
    'Eat only true insects; leave other creepy-crawlies.',
    ['Ant', 'Bee', 'Beetle', 'Butterfly', 'Dragonfly', 'Grasshopper', 'Ladybug', 'Mosquito', 'Moth', 'Wasp', 'Cricket', 'Termite'],
    ['Spider', 'Scorpion', 'Tick', 'Mite', 'Tarantula', 'Centipede', 'Millipede', 'Woodlouse', 'Crab', 'Lobster', 'Shrimp', 'Snail', 'Slug', 'Earthworm', 'Leech', 'Harvestman', 'Roundworm', 'Tapeworm', 'Tardigrade'],
  ),
  board(
    'Greek Gods',
    'mythology::greek_gods',
    'Eat only Greek gods; leave Roman gods.',
    ['Zeus', 'Hera', 'Poseidon', 'Hades', 'Athena', 'Artemis', 'Ares', 'Aphrodite', 'Hermes', 'Hephaestus', 'Demeter', 'Dionysus'],
    ['Jupiter', 'Juno', 'Neptune', 'Pluto', 'Minerva', 'Diana', 'Mars', 'Venus', 'Mercury', 'Vulcan', 'Ceres', 'Bacchus', 'Saturn', 'Cupid', 'Janus', 'Faunus', 'Luna', 'Proserpina', 'Sol'],
  ),
  board(
    'Countries in Europe',
    'geography::countries_europe',
    'Eat only countries in Europe',
    ['France', 'Spain', 'Italy', 'Germany', 'Poland', 'Greece', 'Sweden', 'Norway', 'Portugal', 'Ireland', 'Austria', 'Finland'],
    [
      'Brazil',
      'Egypt',
      'Japan',
      'Peru',
      'Kenya',
      'Nepal',
      'Chile',
      'Cuba',
      'Iran',
      'Mali',
      'Fiji',
      'Laos',
      'India',
      'China',
      'Mexico',
      'Canada',
      'Ghana',
      'Qatar',
      'Tonga',
      'Australia',
      'Argentina',
      'Morocco',
      'Thailand',
      'Vietnam',
      'Colombia',
    ],
  ),
  board(
    'Planets in our solar system',
    'astronomy::solar_system_planets',
    'Eat only planets in our solar system',
    ['Mercury', 'Venus', 'Earth', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune'],
    [
      'Pluto',
      'Moon',
      'Sun',
      'Ceres',
      'Eris',
      'Titan',
      'Europa',
      'Halley',
      'Sirius',
      'Vega',
      'Ganymede',
      'Phobos',
      'Io',
      'Callisto',
      'Deimos',
      'Makemake',
      'Comet',
      'Asteroid',
      'Polaris',
      'Haumea',
      'Charon',
      'Triton',
      'Enceladus',
      'Pallas',
      'Vesta',
      'Quaoar',
      'Oberon',
      'Rhea',
    ],
  ),
  board(
    'Elements that are gases',
    'chemistry::elements_gas_stp',
    'Eat only elements that are gases at room temperature',
    ['Hydrogen', 'Helium', 'Nitrogen', 'Oxygen', 'Fluorine', 'Neon', 'Chlorine', 'Argon', 'Krypton', 'Xenon', 'Radon'],
    ['Carbon', 'Iron', 'Gold', 'Sodium', 'Sulfur', 'Mercury', 'Bromine', 'Lead', 'Silver', 'Copper', 'Zinc', 'Tin', 'Calcium', 'Nickel', 'Cobalt', 'Silicon', 'Boron', 'Iodine', 'Phosphorus'],
  ),
  board(
    'Even numbers',
    'math::even_numbers',
    'Eat only even numbers',
    ['2', '4', '6', '8', '10', '12', '14', '16', '18', '20', '22', '24'],
    ['1', '3', '5', '7', '9', '11', '13', '15', '17', '19', '21', '23', '25', '27', '29', '31', '33', '35', '37'],
  ),
  board(
    'U.S. state capitals',
    'geography::us_state_capitals',
    'Eat only U.S. state capitals',
    ['Austin', 'Boston', 'Denver', 'Albany', 'Phoenix', 'Atlanta', 'Helena', 'Salem', 'Boise', 'Topeka', 'Pierre', 'Dover'],
    ['Chicago', 'Houston', 'Dallas', 'Miami', 'Seattle', 'Portland', 'Detroit', 'Buffalo', 'Reno', 'Tampa', 'Tulsa', 'Newark', 'Las Vegas', 'San Diego', 'Orlando', 'Memphis', 'Milwaukee', 'Anaheim', 'Spokane'],
  ),
  board(
    'Prime numbers',
    'math::prime_numbers',
    'Eat only prime numbers',
    ['2', '3', '5', '7', '11', '13', '17', '19', '23', '29', '31', '37'],
    ['1', '4', '6', '8', '9', '15', '21', '25', '27', '33', '35', '49', '39', '51', '55', '57', '63', '77', '91'],
  ),
  board(
    'Shakespeare plays',
    'literature::shakespeare_plays',
    'Eat only plays written by Shakespeare',
    ['Hamlet', 'Macbeth', 'Othello', 'King Lear', 'Twelfth Night', 'Henry V', 'Richard III', 'Julius Caesar', 'The Tempest', 'Coriolanus', 'Cymbeline', 'Pericles'],
    ['Faust', 'Medea', 'Oedipus', 'Tartuffe', 'Endgame', 'Pygmalion', 'Antigone', 'Lysistrata', 'The Crucible', 'Godot', 'Hedda Gabler', "A Doll's House", 'Our Town', 'The Seagull', 'Ghosts', 'Volpone', 'The Misanthrope', 'Electra', 'Phaedra'],
  ),
  board(
    'Mammals',
    'biology::mammals',
    'Eat only mammals',
    ['Dolphin', 'Bat', 'Whale', 'Platypus', 'Kangaroo', 'Tiger', 'Wolf', 'Horse', 'Bear', 'Otter', 'Seal', 'Koala'],
    ['Shark', 'Penguin', 'Eagle', 'Crocodile', 'Tuna', 'Ostrich', 'Frog', 'Octopus', 'Salmon', 'Turtle', 'Snake', 'Newt', 'Lizard', 'Sparrow', 'Jellyfish', 'Lobster', 'Toad', 'Cobra', 'Falcon'],
  ),
  board(
    'Countries of Africa',
    'geography::countries_africa',
    'Eat only African countries; leave the rest.',
    ['Nigeria', 'Kenya', 'Egypt', 'Ghana', 'Morocco', 'Ethiopia', 'Tanzania', 'Algeria', 'Senegal', 'Uganda', 'Zambia', 'Angola'],
    ['Yemen', 'Saudi Arabia', 'Jordan', 'Oman', 'Iraq', 'Iran', 'Guyana', 'Suriname', 'Pakistan', 'Afghanistan', 'Sri Lanka', 'Nepal', 'Bolivia', 'Paraguay', 'Cambodia', 'Indonesia', 'Honduras', 'Lebanon', 'Papua New Guinea'],
  ),
  board(
    'Landlocked Countries',
    'geography::landlocked_countries',
    'Eat only landlocked countries; leave coastal ones.',
    ['Switzerland', 'Austria', 'Hungary', 'Nepal', 'Bolivia', 'Paraguay', 'Mongolia', 'Mali', 'Chad', 'Zimbabwe', 'Zambia', 'Afghanistan'],
    ['Peru', 'Chile', 'Italy', 'Germany', 'France', 'India', 'China', 'Iran', 'Pakistan', 'Mozambique', 'Angola', 'Senegal', 'Argentina', 'Brazil', 'Croatia', 'Slovenia', 'Romania', 'Vietnam', 'Myanmar'],
  ),
  board(
    'Island Nations',
    'geography::island_nations',
    'Eat only island nations; leave mainland countries.',
    ['Japan', 'Iceland', 'Cuba', 'Madagascar', 'Sri Lanka', 'New Zealand', 'Jamaica', 'Philippines', 'Malta', 'Fiji', 'Maldives', 'Bahamas'],
    ['Italy', 'South Korea', 'Greece', 'India', 'Vietnam', 'Thailand', 'Malaysia', 'Norway', 'Portugal', 'Denmark', 'Mexico', 'Panama', 'Saudi Arabia', 'Oman', 'Brazil', 'Sweden', 'Turkey', 'Croatia', 'Chile'],
  ),
  board(
    'US States on the Coast',
    'geography::us_coastal_states',
    'Eat only US states on an ocean; leave landlocked ones.',
    ['California', 'Oregon', 'Washington', 'Alaska', 'Hawaii', 'Florida', 'New York', 'New Jersey', 'Massachusetts', 'Maine', 'Virginia', 'South Carolina'],
    ['Nevada', 'Arizona', 'Utah', 'Colorado', 'Nebraska', 'Kansas', 'Oklahoma', 'Iowa', 'Missouri', 'Arkansas', 'Kentucky', 'Tennessee', 'West Virginia', 'Idaho', 'Montana', 'Wyoming', 'South Dakota', 'New Mexico', 'Vermont'],
  ),
  board(
    'Capitals of South America',
    'geography::south_america_capitals',
    'Eat only South American capitals; leave other cities.',
    ['Buenos Aires', 'Brasilia', 'Santiago', 'Bogota', 'Quito', 'Georgetown', 'Asuncion', 'Lima', 'Paramaribo', 'Montevideo', 'Caracas'],
    ['Sao Paulo', 'Rio de Janeiro', 'Medellin', 'Cali', 'Guayaquil', 'Cordoba', 'Rosario', 'Valparaiso', 'Cusco', 'Arequipa', 'Maracaibo', 'Cartagena', 'Salvador', 'Belo Horizonte', 'Barranquilla', 'Mendoza', 'Manaus', 'Concepcion', 'Maracay'],
  ),
  board(
    'Capitals of Europe',
    'geography::european_capitals',
    'Eat only European capitals; leave other cities.',
    ['Paris', 'London', 'Berlin', 'Madrid', 'Rome', 'Vienna', 'Lisbon', 'Warsaw', 'Athens', 'Stockholm', 'Dublin', 'Budapest'],
    ['Barcelona', 'Munich', 'Hamburg', 'Milan', 'Naples', 'Frankfurt', 'Marseille', 'Lyon', 'Manchester', 'Birmingham', 'Rotterdam', 'The Hague', 'Valencia', 'Seville', 'Porto', 'Gothenburg', 'Krakow', 'Zurich', 'Geneva'],
  ),
  board(
    'Countries of Asia',
    'geography::countries_asia',
    'Eat only countries in Asia; leave other continents.',
    ['Japan', 'China', 'India', 'Thailand', 'Vietnam', 'South Korea', 'Mongolia', 'Nepal', 'Philippines', 'Pakistan', 'Sri Lanka', 'Cambodia'],
    ['Brazil', 'France', 'Germany', 'Nigeria', 'Kenya', 'Mexico', 'Canada', 'Australia', 'New Zealand', 'Peru', 'Chile', 'Spain', 'Italy', 'Greece', 'Morocco', 'Ethiopia', 'Argentina', 'Sweden', 'Poland'],
  ),
  board(
    'US National Parks',
    'geography::us_national_parks',
    'Eat only US National Parks; leave cities and monuments.',
    ['Yellowstone', 'Yosemite', 'Grand Canyon', 'Zion', 'Acadia', 'Everglades', 'Glacier', 'Sequoia', 'Denali', 'Arches', 'Olympic', 'Bryce Canyon'],
    ['Chicago', 'Boston', 'Seattle', 'Denver', 'Miami', 'Phoenix', 'Las Vegas', 'Atlanta', 'Nashville', 'Portland', 'San Diego', 'Salt Lake City', 'Washington DC', 'Statue of Liberty', 'Mount Rushmore', 'Devils Tower', 'Muir Woods', 'Niagara Falls', 'Fort McHenry'],
  ),
  board(
    'Eurozone Countries',
    'geography::eurozone_countries',
    'Eat only countries using the euro; leave non-euro Europe.',
    ['Germany', 'France', 'Italy', 'Spain', 'Portugal', 'Greece', 'Ireland', 'Austria', 'Belgium', 'Netherlands', 'Finland', 'Slovakia'],
    ['UK', 'Switzerland', 'Sweden', 'Norway', 'Denmark', 'Poland', 'Czechia', 'Hungary', 'Romania', 'Iceland', 'Ukraine', 'Serbia', 'Albania', 'North Macedonia', 'Bosnia', 'Moldova', 'Belarus', 'Liechtenstein'],
  ),
  board(
    'Metals on the Periodic Table',
    'science::metallic_elements',
    'Eat only metal elements; leave nonmetals.',
    ['Iron', 'Copper', 'Gold', 'Silver', 'Aluminum', 'Lead', 'Zinc', 'Nickel', 'Sodium', 'Calcium', 'Magnesium', 'Titanium'],
    ['Oxygen', 'Hydrogen', 'Nitrogen', 'Carbon', 'Sulfur', 'Chlorine', 'Fluorine', 'Helium', 'Neon', 'Argon', 'Phosphorus', 'Bromine', 'Iodine', 'Krypton', 'Xenon', 'Selenium', 'Radon', 'Boron', 'Silicon'],
  ),
  board(
    'Bones of the Human Body',
    'science::human_bones',
    'Eat only bones found in the human body; leave human muscles.',
    ['Femur', 'Tibia', 'Fibula', 'Humerus', 'Radius', 'Ulna', 'Sternum', 'Clavicle', 'Scapula', 'Patella', 'Mandible', 'Rib'],
    ['Biceps', 'Triceps', 'Deltoid', 'Trapezius', 'Pectoralis', 'Quadriceps', 'Hamstring', 'Gluteus', 'Soleus', 'Masseter', 'Sartorius', 'Diaphragm', 'Oblique', 'Tibialis', 'Brachialis', 'Gastrocnemius', 'Latissimus', 'Rhomboid', 'Adductor'],
  ),
  board(
    'Vitamins vs Minerals',
    'science::vitamins_not_minerals',
    'Eat only essential vitamins; leave dietary minerals.',
    ['Vitamin A', 'Vitamin C', 'Vitamin D', 'Vitamin E', 'Vitamin K', 'Vitamin B6', 'Vitamin B12', 'Thiamine', 'Riboflavin', 'Niacin', 'Folate', 'Biotin'],
    ['Calcium', 'Iron', 'Zinc', 'Magnesium', 'Potassium', 'Sodium', 'Phosphorus', 'Iodine', 'Selenium', 'Copper', 'Manganese', 'Chromium', 'Fluoride', 'Molybdenum', 'Chloride', 'Cobalt', 'Sulfur', 'Boron', 'Silicon'],
  ),
  board(
    'One-Letter Element Symbols',
    'science::one_letter_element_symbols',
    'Eat only elements with a 1-letter symbol; leave 2-letter.',
    ['Hydrogen', 'Oxygen', 'Carbon', 'Nitrogen', 'Sulfur', 'Phosphorus', 'Potassium', 'Iodine', 'Boron', 'Fluorine', 'Tungsten', 'Uranium'],
    ['Helium', 'Calcium', 'Chlorine', 'Cobalt', 'Copper', 'Sodium', 'Nickel', 'Neon', 'Osmium', 'Platinum', 'Silicon', 'Selenium', 'Magnesium', 'Aluminum', 'Iron', 'Zinc', 'Lithium', 'Bromine', 'Beryllium'],
  ),
  board(
    'Viral vs Bacterial Diseases',
    'science::viral_diseases',
    'Eat only viral diseases; leave bacterial ones.',
    ['Influenza', 'COVID-19', 'Measles', 'Mumps', 'Rubella', 'Chickenpox', 'Smallpox', 'Polio', 'Rabies', 'Ebola', 'Yellow fever', 'HIV/AIDS'],
    ['Tuberculosis', 'Cholera', 'Tetanus', 'Typhoid fever', 'Strep throat', 'Whooping cough', 'Diphtheria', 'Bubonic plague', 'Leprosy', 'Lyme disease', 'Gonorrhea', 'Syphilis', 'Botulism', 'Anthrax', 'Scarlet fever', 'Chlamydia', 'Salmonella', "Legionnaires'", 'Gas gangrene'],
  ),
  board(
    'Organs of the Human Body',
    'science::human_organs',
    'Eat only organs of the human body; leave bones.',
    ['Heart', 'Brain', 'Liver', 'Lungs', 'Kidneys', 'Stomach', 'Pancreas', 'Spleen', 'Skin', 'Bladder', 'Gallbladder', 'Small intestine'],
    ['Femur', 'Tibia', 'Fibula', 'Humerus', 'Radius', 'Ulna', 'Skull', 'Sternum', 'Clavicle', 'Scapula', 'Pelvis', 'Patella', 'Mandible', 'Rib', 'Vertebra', 'Coccyx', 'Sacrum', 'Hyoid', 'Metacarpal'],
  ),
  board(
    'US Presidents',
    'history::us_presidents',
    'Eat only US Presidents; leave those who never were.',
    ['George Washington', 'Thomas Jefferson', 'Abraham Lincoln', 'Theodore Roosevelt', 'John F. Kennedy', 'Ronald Reagan', 'Barack Obama', 'James Madison', 'Andrew Jackson', 'Harry Truman', 'Woodrow Wilson', 'Ulysses S. Grant'],
    ['Benjamin Franklin', 'Alexander Hamilton', 'John Jay', 'Patrick Henry', 'Samuel Adams', 'Thomas Paine', 'Aaron Burr', 'John Hancock', 'Henry Clay', 'Daniel Webster', 'John C. Calhoun', 'Stephen A. Douglas', 'Frederick Douglass', 'Robert E. Lee', 'Jefferson Davis', 'Gouverneur Morris', 'Roger Sherman', 'Charles Sumner', 'John Marshall'],
  ),
  board(
    'The Thirteen Colonies',
    'history::thirteen_colonies',
    'Eat only the original 13 Colonies; leave other US states.',
    ['Delaware', 'Pennsylvania', 'New Jersey', 'Georgia', 'Connecticut', 'Massachusetts', 'Maryland', 'South Carolina', 'New Hampshire', 'Virginia', 'New York', 'North Carolina'],
    ['Vermont', 'Maine', 'Kentucky', 'Tennessee', 'Ohio', 'Florida', 'Texas', 'California', 'Michigan', 'Illinois', 'Indiana', 'Alabama', 'Mississippi', 'Louisiana', 'Missouri', 'West Virginia', 'Wisconsin', 'Arkansas', 'Iowa'],
  ),
  board(
    'Ancient Civilizations',
    'history::ancient_civilizations',
    'Eat only ancient civilizations; leave modern countries.',
    ['Mesopotamia', 'Sumer', 'Babylonia', 'Assyria', 'Ancient Greece', 'Ancient Rome', 'Phoenicia', 'Carthage', 'Indus Valley', 'Hittites', 'Minoans', 'Maya'],
    ['Iraq', 'Iran', 'Italy', 'Turkey', 'Mexico', 'Peru', 'Pakistan', 'Germany', 'France', 'Brazil', 'Canada', 'Japan', 'Australia', 'Russia', 'United States', 'Argentina', 'Nigeria', 'Indonesia', 'Sweden'],
  ),
  board(
    'Former British Empire',
    'history::british_empire',
    'Eat only former British Empire countries; leave the rest.',
    ['India', 'Australia', 'Canada', 'New Zealand', 'South Africa', 'Nigeria', 'Kenya', 'Ireland', 'Jamaica', 'Pakistan', 'Singapore', 'Ghana'],
    ['Germany', 'Japan', 'Brazil', 'Mexico', 'Thailand', 'Ethiopia', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Switzerland', 'Austria', 'Poland', 'Colombia', 'Chile', 'Vietnam', 'South Korea', 'Mongolia', 'Russia'],
  ),
  board(
    'Roman Emperors',
    'history::roman_emperors',
    'Eat only Roman emperors; leave Greek philosophers.',
    ['Augustus', 'Tiberius', 'Caligula', 'Claudius', 'Nero', 'Vespasian', 'Titus', 'Trajan', 'Hadrian', 'Marcus Aurelius', 'Commodus', 'Constantine'],
    ['Socrates', 'Plato', 'Aristotle', 'Pythagoras', 'Epicurus', 'Anaximenes', 'Diogenes', 'Heraclitus', 'Democritus', 'Thales', 'Anaximander', 'Anaxagoras', 'Parmenides', 'Empedocles', 'Protagoras', 'Epictetus', 'Plotinus', 'Gorgias', 'Theophrastus'],
  ),
  board(
    'Empires That No Longer Exist',
    'history::fallen_empires',
    'Eat only fallen empires; leave present-day countries.',
    ['Roman Empire', 'Ottoman Empire', 'Mongol Empire', 'Byzantine Empire', 'Aztec Empire', 'Inca Empire', 'Persian Empire', 'Holy Roman Empire', 'Assyrian Empire', 'Babylonian Empire', 'Mali Empire', 'Mughal Empire'],
    ['France', 'China', 'Italy', 'Iran', 'Turkey', 'Greece', 'Egypt', 'India', 'Mexico', 'Peru', 'Iraq', 'Mongolia', 'United Kingdom', 'Russia', 'Spain', 'Austria', 'Hungary', 'Germany', 'Ethiopia'],
  ),
  board(
    'Summer Games Sports',
    'sports::summer_games_sports',
    'Eat only Summer Games sports; leave Winter ones.',
    ['Swimming', 'Gymnastics', 'Basketball', 'Boxing', 'Cycling', 'Rowing', 'Fencing', 'Archery', 'Weightlifting', 'Judo', 'Volleyball', 'Diving'],
    ['Figure Skating', 'Ice Hockey', 'Alpine Skiing', 'Snowboarding', 'Bobsled', 'Luge', 'Curling', 'Speed Skating', 'Ski Jumping', 'Biathlon', 'Cross-Country Ski', 'Skeleton', 'Freestyle Skiing', 'Short Track', 'Nordic Combined', 'Moguls', 'Slopestyle', 'Halfpipe'],
  ),
  board(
    'Metric Units',
    'science::metric_units',
    // #10: drop the "(SI)" parenthetical — Liter and Celsius are metric but NOT strictly SI, so "(SI)"
    // made two of the board's own correct answers technically-wrong. The board tests metric-vs-imperial.
    'Eat only metric units; leave imperial units.',
    ['Meter', 'Kilogram', 'Liter', 'Celsius', 'Gram', 'Kilometer', 'Centimeter', 'Milliliter', 'Newton', 'Watt', 'Joule', 'Pascal'],
    ['Mile', 'Pound', 'Ounce', 'Inch', 'Foot', 'Yard', 'Gallon', 'Quart', 'Pint', 'Fahrenheit', 'Acre', 'Furlong', 'Stone', 'Fluid Ounce', 'Cup', 'Tablespoon', 'Bushel', 'Horsepower', 'League'],
  ),
  board(
    'Perfect Cubes',
    'math::perfect_cubes',
    'Eat only perfect cubes; leave non-cubes.',
    ['1', '8', '27', '64', '125', '216', '343', '512', '729', '1000', '1331', '1728'],
    ['4', '9', '16', '36', '49', '100', '144', '200', '256', '400', '500', '625', '900', '1024', '1200', '1500'],
  ),
  board(
    'Winter Games Sports',
    'sports::winter_games_sports',
    'Eat only Winter Games sports; leave Summer ones.',
    ['Figure Skating', 'Ice Hockey', 'Alpine Skiing', 'Snowboarding', 'Bobsled', 'Luge', 'Curling', 'Speed Skating', 'Ski Jumping', 'Biathlon', 'Cross-Country Ski', 'Skeleton'],
    ['Swimming', 'Track & Field', 'Gymnastics', 'Basketball', 'Boxing', 'Cycling', 'Rowing', 'Fencing', 'Archery', 'Weightlifting', 'Judo', 'Volleyball', 'Tennis', 'Diving', 'Sailing', 'Wrestling', 'Table Tennis', 'Badminton', 'Handball'],
  ),
  board(
    'Soccer Host Nations',
    'sports::soccer_host_nations',
    'Eat only world-tournament soccer host nations; leave non-hosts.',
    ['Brazil', 'France', 'Germany', 'Italy', 'Argentina', 'Spain', 'Mexico', 'USA', 'South Korea', 'Russia', 'Qatar', 'Uruguay'],
    ['Greece', 'Netherlands', 'Belgium', 'Finland', 'Norway', 'Austria', 'Portugal', 'Poland', 'Ukraine', 'Denmark', 'Croatia', 'Hungary', 'Turkey', 'Scotland', 'Ireland', 'Romania', 'Bulgaria', 'Serbia'],
  ),
  board(
    'Ball Sports',
    'sports::ball_sports',
    'Eat only ball sports; leave ball-free sports.',
    ['Soccer', 'Basketball', 'Tennis', 'Golf', 'Baseball', 'Volleyball', 'Cricket', 'Rugby', 'Bowling', 'Water Polo', 'Handball', 'Softball'],
    ['Ice Hockey', 'Badminton', 'Boxing', 'Swimming', 'Archery', 'Fencing', 'Wrestling', 'Curling', 'Darts', 'Skiing', 'Cycling', 'Rowing', 'Judo', 'Karate', 'Figure Skating', 'Surfing', 'Weightlifting', 'Sprinting'],
  ),
  board(
    'Coffee Drinks',
    'food::coffee_drinks',
    'Eat only coffee drinks; leave teas.',
    ['Espresso', 'Latte', 'Cappuccino', 'Macchiato', 'Americano', 'Mocha', 'Flat White', 'Cortado', 'Ristretto', 'Affogato', 'Frappe', 'Cold Brew'],
    ['Green Tea', 'Black Tea', 'Oolong', 'Chamomile', 'Earl Grey', 'Matcha', 'Chai', 'Rooibos', 'Peppermint', 'Jasmine', 'Darjeeling', 'White Tea', 'Hibiscus', 'Ginger Tea', 'Lemongrass', 'Sencha', 'Assam', 'Pu-erh'],
  ),
  board(
    'String Instruments',
    'music::string_instruments',
    'Eat only string instruments; leave wind instruments.',
    ['Violin', 'Cello', 'Guitar', 'Viola', 'Double Bass', 'Harp', 'Banjo', 'Mandolin', 'Ukulele', 'Sitar', 'Lute', 'Bass Guitar'],
    ['Flute', 'Clarinet', 'Trumpet', 'Saxophone', 'Trombone', 'Tuba', 'Oboe', 'Bassoon', 'French Horn', 'Piccolo', 'Harmonica', 'Recorder', 'Bagpipes', 'Cornet', 'Pan Flute', 'Didgeridoo', 'Accordion', 'Bugle'],
  ),
  board(
    'Cloud Types',
    'science::cloud_types',
    'Eat only cloud types; leave weather phenomena.',
    ['Cumulus', 'Stratus', 'Cirrus', 'Nimbus', 'Cumulonimbus', 'Altostratus', 'Cirrostratus', 'Stratocumulus', 'Altocumulus', 'Cirrocumulus', 'Nimbostratus', 'Mammatus'],
    ['Rain', 'Snow', 'Hail', 'Sleet', 'Drizzle', 'Fog', 'Mist', 'Frost', 'Dew', 'Thunder', 'Lightning', 'Tornado', 'Hurricane', 'Blizzard', 'Monsoon', 'Typhoon', 'Gale', 'Rainbow'],
  ),
  board(
    'Powers of Two',
    'math::powers_of_two',
    'Eat only powers of two; leave other numbers.',
    ['2', '4', '8', '16', '32', '64', '128', '256', '512', '1024', '2048', '4096'],
    ['3', '6', '10', '12', '20', '24', '36', '48', '96', '100', '144', '192', '200', '500', '768', '1000', '1536', '3000'],
  ),
  board(
    'Music Genres',
    'music::music_genres',
    'Eat only music genres; leave styles of dance.',
    ['Jazz', 'Blues', 'Reggae', 'Grunge', 'Heavy Metal', 'Bluegrass', 'Dubstep', 'Gospel', 'Techno', 'Punk Rock', 'Opera', 'Ska'],
    ['Ballet', 'Foxtrot', 'Quickstep', 'Charleston', 'Twist', 'Breakdance', 'Lindy Hop', 'Jitterbug', 'Hustle', 'Krumping', 'Voguing', 'Hula', 'Can-Can', 'Square Dance', 'Highland Fling', 'Tap Dance', 'Morris Dance', 'Belly Dance', 'Pole Dance'],
  ),
  board(
    'Marsupials',
    'biology::marsupials',
    'Eat only marsupials; leave placental mammals.',
    ['Kangaroo', 'Koala', 'Wombat', 'Opossum', 'Tasmanian Devil', 'Wallaby', 'Quokka', 'Bandicoot', 'Sugar Glider', 'Numbat', 'Bilby', 'Quoll'],
    ['Squirrel', 'Rabbit', 'Deer', 'Beaver', 'Otter', 'Raccoon', 'Ferret', 'Meerkat', 'Lemur', 'Sloth', 'Armadillo', 'Hedgehog', 'Mole', 'Chipmunk', 'Hyena', 'Badger', 'Weasel', 'Mongoose', 'Aardvark'],
  ),
  board(
    'Percussion Instruments',
    'music::percussion_instruments',
    'Eat only percussion instruments; leave string instruments.',
    ['Snare Drum', 'Timpani', 'Xylophone', 'Marimba', 'Vibraphone', 'Glockenspiel', 'Cymbals', 'Triangle', 'Tambourine', 'Bongos', 'Maracas', 'Djembe'],
    ['Violin', 'Viola', 'Cello', 'Double Bass', 'Guitar', 'Harp', 'Banjo', 'Mandolin', 'Ukulele', 'Sitar', 'Lute', 'Fiddle', 'Bass Guitar', 'Zither', 'Balalaika', 'Erhu', 'Koto', 'Lyre', 'Bouzouki'],
  ),
  board(
    'Woodwind Instruments',
    'music::woodwind_instruments',
    'Eat only woodwind instruments; leave brass instruments.',
    ['Flute', 'Clarinet', 'Oboe', 'Bassoon', 'Saxophone', 'Piccolo', 'Recorder', 'English Horn', 'Bass Clarinet', 'Contrabassoon', 'Pan Flute', 'Bagpipes'],
    ['Trumpet', 'Trombone', 'Tuba', 'French Horn', 'Cornet', 'Flugelhorn', 'Euphonium', 'Bugle', 'Sousaphone', 'Baritone Horn', 'Mellophone', 'Piccolo Trumpet', 'Bass Trombone', 'Wagner Tuba', 'Pocket Trumpet', 'Tenor Horn', 'Valve Trombone', 'Natural Horn', 'Alto Horn'],
  ),
  board(
    'Natural Fibers',
    'science::natural_fibers',
    'Eat only natural fibers; leave synthetic fibers.',
    ['Cotton', 'Wool', 'Silk', 'Linen', 'Cashmere', 'Hemp', 'Jute', 'Angora', 'Mohair', 'Alpaca', 'Camel Hair', 'Ramie'],
    // #10: 'Fleece' → 'Modacrylic'. Fleece reads as sheep's wool (a NATURAL fiber) to most players, so it
    // was an unfair trap; Modacrylic is an unambiguous fully-synthetic fiber. (Rayon/Modal/Acetate/Viscose
    // rejected — semi-synthetic, equally ambiguous.) Count/shape unchanged (still 19 decoys).
    ['Polyester', 'Nylon', 'Acrylic', 'Spandex', 'Kevlar', 'Lycra', 'Modacrylic', 'Microfiber', 'Olefin', 'Vinyl', 'Polyethylene', 'Elastane', 'Neoprene', 'Polypropylene', 'Nomex', 'Aramid', 'Dacron', 'Orlon', 'PVC'],
  ),
  board(
    'Best Picture Winners',
    'film::best_picture_winners',
    'Eat only Best Picture winners; leave mere nominees.',
    ['Titanic', 'Gladiator', 'Forrest Gump', 'The Godfather', 'Casablanca', 'Parasite', 'Oppenheimer', 'Braveheart', 'Rocky', 'Amadeus', 'Argo', 'The Departed'],
    ['Pulp Fiction', 'La La Land', 'Avatar', 'Shawshank', 'Goodfellas', 'Fargo', 'Saving Private Ryan', 'Star Wars', 'E.T.', 'Inception', 'Up', 'Mad Max Fury Road', 'Get Out', 'Dunkirk', 'Sideways', 'The Irishman', 'Black Panther', 'American Hustle', 'Top Gun Maverick'],
  ),
  board(
    'Citrus Fruits',
    'food::citrus_fruits',
    'Eat only citrus fruits; leave berries.',
    ['Orange', 'Lemon', 'Lime', 'Grapefruit', 'Tangerine', 'Mandarin', 'Clementine', 'Kumquat', 'Pomelo', 'Citron', 'Bergamot', 'Yuzu'],
    ['Strawberry', 'Blueberry', 'Raspberry', 'Blackberry', 'Cranberry', 'Gooseberry', 'Elderberry', 'Mulberry', 'Boysenberry', 'Currant', 'Huckleberry', 'Lingonberry', 'Cloudberry', 'Acai', 'Goji Berry', 'Bilberry', 'Loganberry', 'Marionberry', 'Salmonberry'],
  ),
  board(
    'Horror Movies',
    'film::horror_movies',
    'Eat only horror movies; leave comedy movies.',
    ['The Exorcist', 'Halloween', 'The Conjuring', 'Hereditary', 'The Shining', 'Insidious', 'The Ring', 'Saw', 'Psycho', 'Midsommar', 'Sinister', 'The Babadook'],
    ['Superbad', 'Bridesmaids', 'The Hangover', 'Anchorman', 'Step Brothers', 'Dumb and Dumber', 'Borat', 'Scary Movie', 'Groundhog Day', 'Mean Girls', 'Elf', 'The Mask', 'Liar Liar', 'Happy Gilmore', 'Billy Madison', 'Tropic Thunder', '21 Jump Street', 'Caddyshack', 'Austin Powers'],
  ),
  board(
    'Hand Tools',
    'tech::hand_tools',
    'Eat only hand tools; leave power tools.',
    ['Hammer', 'Screwdriver', 'Wrench', 'Pliers', 'Chisel', 'Handsaw', 'Level', 'Tape Measure', 'Mallet', 'File', 'Clamp', 'Hex Key'],
    ['Drill', 'Circular Saw', 'Jigsaw', 'Angle Grinder', 'Sander', 'Router', 'Nail Gun', 'Impact Driver', 'Table Saw', 'Lathe', 'Planer', 'Chainsaw', 'Heat Gun', 'Rotary Tool', 'Belt Sander', 'Bench Grinder', 'Miter Saw', 'Air Compressor', 'Reciprocating Saw'],
  ),
  board(
    'Pasta Shapes',
    'food::pasta_shapes',
    'Eat only pasta shapes; leave types of bread.',
    ['Penne', 'Fusilli', 'Rigatoni', 'Farfalle', 'Linguine', 'Tagliatelle', 'Orzo', 'Rotini', 'Ziti', 'Macaroni', 'Vermicelli', 'Bucatini'],
    ['Baguette', 'Ciabatta', 'Sourdough', 'Focaccia', 'Rye', 'Pita', 'Naan', 'Brioche', 'Bagel', 'Pumpernickel', 'Croissant', 'Cornbread', 'Challah', 'Tortilla', 'Pretzel', 'Multigrain', 'Flatbread', 'Crumpet'],
  ),
  board(
    'Amphibians',
    'biology::amphibians',
    'Eat only amphibians; leave reptiles.',
    ['Frog', 'Toad', 'Salamander', 'Newt', 'Axolotl', 'Caecilian', 'Bullfrog', 'Tree Frog', 'Fire Salamander', 'Mudpuppy', 'Hellbender', 'Poison Dart Frog'],
    ['Turtle', 'Tortoise', 'Snake', 'Lizard', 'Gecko', 'Iguana', 'Chameleon', 'Crocodile', 'Alligator', 'Skink', 'Komodo Dragon', 'Cobra', 'Python', 'Gila Monster', 'Tuatara', 'Monitor Lizard', 'Anole', 'Terrapin'],
  ),
  board(
    'Deserts',
    'geography::deserts',
    'Eat only deserts; leave mountain ranges.',
    ['Sahara', 'Gobi', 'Kalahari', 'Mojave', 'Atacama', 'Sonoran', 'Namib', 'Arabian', 'Patagonian', 'Thar', 'Karakum', 'Chihuahuan'],
    ['Andes', 'Alps', 'Himalayas', 'Rockies', 'Urals', 'Pyrenees', 'Appalachians', 'Caucasus', 'Atlas', 'Carpathians', 'Sierra Nevada', 'Cascades', 'Balkans', 'Apennines', 'Zagros', 'Drakensberg', 'Pennines', 'Tian Shan', 'Great Dividing'],
  ),
  board(
    'Sitcoms vs Dramas',
    'tv::sitcoms_not_dramas',
    'Eat only sitcoms; leave drama series.',
    ['Friends', 'Seinfeld', 'The Office', 'Cheers', 'Frasier', 'Parks and Rec', 'Brooklyn 99', 'Big Bang Theory', 'Modern Family', '30 Rock', 'Community', 'Always Sunny'],
    ['Breaking Bad', 'The Sopranos', 'The Wire', 'Mad Men', 'Game of Thrones', 'The Crown', 'Better Call Saul', 'Lost', 'The Walking Dead', "Grey's Anatomy", 'House', 'ER', 'The West Wing', 'Stranger Things', 'Ozark', 'Succession', 'Westworld', 'Dexter', 'Homeland'],
  ),
  board(
    'Baseball Positions',
    'sports::baseball_positions',
    'Eat only baseball field positions; leave other sports.',
    ['Pitcher', 'Catcher', 'First Baseman', 'Second Baseman', 'Third Baseman', 'Shortstop', 'Left Fielder', 'Center Fielder', 'Right Fielder'],
    ['Quarterback', 'Goalkeeper', 'Point Guard', 'Striker', 'Fullback', 'Winger', 'Libero', 'Setter', 'Midfielder', 'Defenseman', 'Halfback', 'Linebacker', 'Sweeper', 'Scrum-half', 'Cornerback', 'Power Forward', 'Goaltender', 'Wide Receiver', 'Small Forward'],
  ),
  board(
    'Reality Competition Shows',
    'tv::reality_competition',
    'Eat only reality competition shows; leave scripted dramas.',
    ['Survivor', 'Big Brother', 'The Amazing Race', 'American Idol', 'The Voice', 'Top Chef', "RuPaul's Drag Race", 'Project Runway', "America's Got Talent", 'MasterChef', "Hell's Kitchen", 'The Challenge'],
    ['Breaking Bad', 'The Sopranos', 'The Wire', 'Mad Men', 'Game of Thrones', 'Better Call Saul', 'The Crown', 'Stranger Things', "Grey's Anatomy", 'House of Cards', 'The Walking Dead', 'Lost', 'Ozark', 'Succession', 'This Is Us', 'Yellowstone', 'Westworld', 'The Last of Us', 'Euphoria'],
  ),
  // S8 replacement (was tv::anime_series — franchise titles as content, IP-REPLACE per ruling 2).
  // Shape preserved: 12 correct / 19 decoy. Decoy design: MOST stars sit inside a correct constellation
  // on the board (Rigel/Betelgeuse in Orion, Vega in Lyra, Deneb in Cygnus, Polaris in Ursa Minor,
  // Antares in Scorpius, Aldebaran in Taurus, Castor/Pollux in Gemini, Regulus in Leo, Mizar/Alcor in
  // Ursa Major); the rest (Sirius, Altair, Arcturus, Capella, Procyon, Canopus, Spica) are famous
  // near-misses in constellations NOT on the board — all still plausible "is this a constellation?" traps.
  board(
    'Constellations',
    'science::constellations',
    'Eat only constellations; leave the individual stars.',
    ['Orion', 'Cassiopeia', 'Ursa Major', 'Ursa Minor', 'Scorpius', 'Lyra', 'Cygnus', 'Taurus', 'Gemini', 'Leo', 'Pegasus', 'Draco'],
    ['Sirius', 'Betelgeuse', 'Vega', 'Polaris', 'Rigel', 'Altair', 'Deneb', 'Antares', 'Aldebaran', 'Arcturus', 'Capella', 'Procyon', 'Canopus', 'Spica', 'Regulus', 'Castor', 'Pollux', 'Mizar', 'Alcor'],
  ),
  board(
    'Fruits vs Vegetables',
    'food::fruits_not_vegetables',
    'Eat only botanical fruits; leave botanical vegetables.',
    ['Tomato', 'Cucumber', 'Bell Pepper', 'Eggplant', 'Pumpkin', 'Zucchini', 'Avocado', 'Olive', 'Apple', 'Banana', 'Grape', 'Orange'],
    ['Carrot', 'Potato', 'Lettuce', 'Spinach', 'Broccoli', 'Cauliflower', 'Celery', 'Onion', 'Garlic', 'Cabbage', 'Kale', 'Beetroot', 'Radish', 'Sweet Potato', 'Asparagus', 'Turnip', 'Ginger', 'Leek', 'Rhubarb'],
  ),
  board(
    'Italian Dishes',
    'food::italian_dishes',
    'Eat only Italian dishes; leave Mexican dishes.',
    ['Lasagna', 'Spaghetti', 'Risotto', 'Carbonara', 'Ravioli', 'Gnocchi', 'Minestrone', 'Bruschetta', 'Tiramisu', 'Fettuccine', 'Cannelloni', 'Osso Buco'],
    ['Tacos', 'Burrito', 'Enchiladas', 'Quesadilla', 'Guacamole', 'Tamales', 'Salsa', 'Tostada', 'Chile Relleno', 'Pozole', 'Mole', 'Elote', 'Carnitas', 'Flautas', 'Sopes', 'Menudo', 'Chilaquiles', 'Birria', 'Barbacoa'],
  ),
  board(
    'Cheeses',
    'food::cheeses_not_wines',
    'Eat only types of cheese; leave types of wine.',
    ['Cheddar', 'Brie', 'Gouda', 'Mozzarella', 'Parmesan', 'Feta', 'Camembert', 'Gruyere', 'Provolone', 'Manchego', 'Ricotta', 'Havarti'],
    ['Merlot', 'Chardonnay', 'Cabernet', 'Pinot Noir', 'Riesling', 'Malbec', 'Shiraz', 'Zinfandel', 'Sauvignon', 'Chianti', 'Prosecco', 'Champagne', 'Rioja', 'Sangiovese', 'Tempranillo', 'Grenache', 'Pinot Grigio', 'Moscato', 'Beaujolais'],
  ),
  board(
    'Spices vs Herbs',
    'food::spices_not_herbs',
    'Eat only cooking spices; leave fresh culinary herbs.',
    ['Cinnamon', 'Cumin', 'Nutmeg', 'Turmeric', 'Paprika', 'Cloves', 'Cardamom', 'Allspice', 'Saffron', 'Mace', 'Fenugreek', 'Star Anise'],
    ['Basil', 'Parsley', 'Cilantro', 'Mint', 'Rosemary', 'Thyme', 'Oregano', 'Sage', 'Dill', 'Chives', 'Tarragon', 'Marjoram', 'Bay leaf', 'Chervil', 'Lemongrass', 'Lovage', 'Savory', 'Sorrel'],
  ),
  board(
    'Fast Food Chains',
    'food::fast_food_chains',
    'Eat only fast-food chains; leave sit-down chains.',
    ["McDonald's", 'Burger King', "Wendy's", 'Taco Bell', 'KFC', 'Subway', 'Chick-fil-A', 'Popeyes', "Arby's", 'Five Guys', 'Sonic', 'In-N-Out'],
    ['Olive Garden', "Applebee's", "Chili's", 'TGI Fridays', 'Red Lobster', 'Outback', "Denny's", 'IHOP', 'Cheesecake Factory', 'Texas Roadhouse', 'Buffalo Wild Wings', 'Cracker Barrel', 'Red Robin', 'Ruby Tuesday', "P.F. Chang's", 'Longhorn', "Carrabba's", 'Waffle House'],
  ),
  board(
    'Classic Cocktails',
    'food::classic_cocktails',
    'Eat only alcoholic cocktails; leave non-alcoholic drinks.',
    ['Margarita', 'Martini', 'Mojito', 'Manhattan', 'Old Fashioned', 'Daiquiri', 'Negroni', 'Cosmopolitan', 'Whiskey Sour', 'Mai Tai', 'Tom Collins', 'Sidecar'],
    ['Shirley Temple', 'Virgin Mojito', 'Lemonade', 'Iced Tea', 'Roy Rogers', 'Arnold Palmer', 'Cola', 'Ginger Ale', 'Orange Juice', 'Root Beer', 'Sweet Tea', 'Cream Soda', 'Hot Chocolate', 'Cranberry Juice', 'Club Soda', 'Tonic Water', 'Apple Juice', 'Sparkling Water'],
  ),
  board(
    'Desserts',
    'food::desserts_not_savory',
    'Eat only desserts; leave savory dishes.',
    ['Cheesecake', 'Brownie', 'Tiramisu', 'Ice Cream', 'Apple Pie', 'Cupcake', 'Macaron', 'Creme Brulee', 'Eclair', 'Donut', 'Baklava', 'Panna Cotta'],
    ['Lasagna', 'Meatloaf', 'Cheeseburger', 'Pizza', 'Tacos', 'Sushi', 'Fried Rice', 'Pork Chops', 'Pot Roast', 'Mac and Cheese', 'Quiche', 'Risotto', 'Chicken Wings', 'Burrito', 'Pad Thai', 'Dumplings', 'Meatballs', 'Nachos'],
  ),
  board(
    'Root Vegetables',
    'food::root_vegetables',
    'Eat only root vegetables; leave leafy greens.',
    ['Carrot', 'Potato', 'Beet', 'Turnip', 'Radish', 'Parsnip', 'Sweet Potato', 'Cassava', 'Rutabaga', 'Ginger', 'Yam', 'Horseradish'],
    ['Lettuce', 'Spinach', 'Kale', 'Cabbage', 'Arugula', 'Chard', 'Collard Greens', 'Bok Choy', 'Watercress', 'Romaine', 'Endive', 'Mustard Greens', 'Iceberg', 'Escarole', 'Radicchio', 'Sorrel', 'Dandelion Greens', 'Napa Cabbage'],
  ),
  board(
    'Tempo Markings',
    'music::tempo_markings',
    'Eat only tempo (speed) marks; leave dynamics (volume).',
    ['Allegro', 'Andante', 'Adagio', 'Presto', 'Largo', 'Moderato', 'Vivace', 'Lento', 'Grave', 'Allegretto', 'Prestissimo', 'Larghetto'],
    ['Forte', 'Piano', 'Fortissimo', 'Pianissimo', 'Mezzo Forte', 'Mezzo Piano', 'Crescendo', 'Decrescendo', 'Diminuendo', 'Sforzando', 'Calando', 'Morendo', 'Perdendosi', 'Smorzando', 'Rinforzando', 'Forzando', 'Pianississimo', 'Fortississimo'],
  ),
  board(
    'First-Person Shooter Games',
    'gaming::fps_games',
    'Eat only first-person shooters; leave fighting games.',
    ['Call of Duty', 'Halo', 'Doom', 'Counter-Strike', 'Battlefield', 'Half-Life', 'Quake', 'Overwatch', 'Valorant', 'Wolfenstein', 'Far Cry', 'Apex Legends'],
    ['Street Fighter', 'Tekken', 'Mortal Kombat', 'Smash Bros', 'Soulcalibur', 'Guilty Gear', 'King of Fighters', 'Injustice', 'BlazBlue', 'Killer Instinct', 'Dead or Alive', 'Virtua Fighter', 'Marvel vs Capcom', 'DB FighterZ', 'Skullgirls', 'Samurai Shodown', 'Darkstalkers', 'Brawlhalla'],
  ),
  board(
    'Types of Sushi',
    'food::sushi_types',
    'Eat only types of sushi; leave other Japanese dishes.',
    ['Nigiri', 'Maki', 'Temaki', 'Uramaki', 'Chirashi', 'Inari', 'Gunkan', 'Futomaki', 'Hosomaki', 'Oshizushi', 'Narezushi', 'Temarizushi'],
    ['Sashimi', 'Ramen', 'Tempura', 'Udon', 'Teriyaki', 'Miso Soup', 'Gyoza', 'Yakitori', 'Edamame', 'Katsu', 'Onigiri', 'Soba', 'Okonomiyaki', 'Takoyaki', 'Mochi', 'Donburi', 'Bento', 'Shabu-shabu'],
  ),
  board(
    'Data Structures',
    'tech::data_structures',
    'Eat only data structures; leave algorithms.',
    ['Array', 'Stack', 'Queue', 'Linked List', 'Hash Table', 'Binary Tree', 'Heap', 'Graph', 'Trie', 'Set', 'Deque', 'Matrix'],
    ['Bubble Sort', 'Quicksort', 'Merge Sort', 'Binary Search', 'Dijkstra', 'Breadth-First', 'Depth-First', 'Recursion', 'Backtracking', 'Greedy', 'Insertion Sort', 'Selection Sort', 'Heapsort', 'Kruskal', 'Prim', 'A-Star', 'Hashing', 'Two Pointer'],
  ),
  board(
    'Indie Games',
    'gaming::indie_games',
    'Eat only indie games; leave AAA blockbuster games.',
    ['Hollow Knight', 'Stardew Valley', 'Celeste', 'Undertale', 'Hades', 'Cuphead', 'Terraria', 'Shovel Knight', 'Dead Cells', 'Slay the Spire', 'Binding of Isaac', 'Vampire Survivors'],
    ['Call of Duty', 'GTA V', "Assassin's Creed", 'The Witcher 3', 'God of War', 'Red Dead 2', 'EA Sports FC', 'Cyberpunk 2077', 'Skyrim', 'Halo', 'Battlefield', 'Far Cry', 'The Last of Us', 'Horizon', 'Spider-Man', 'Madden NFL', 'Destiny 2', 'Final Fantasy'],
  ),
  board(
    'Fighting Game Franchises',
    'gaming::fighting_games',
    'Eat only fighting games; leave racing games.',
    ['Street Fighter', 'Tekken', 'Mortal Kombat', 'Smash Bros', 'Soulcalibur', 'Guilty Gear', 'King of Fighters', 'Injustice', 'BlazBlue', 'Dead or Alive', 'Virtua Fighter', 'Samurai Shodown'],
    ['Mario Kart', 'Gran Turismo', 'Forza', 'Need for Speed', 'Burnout', 'F-Zero', 'Crash Team Racing', 'WipEout', 'DiRT', 'Project CARS', 'Ridge Racer', 'Trackmania', 'Asphalt', 'OutRun', 'Daytona USA', 'MotoGP', 'Diddy Kong Racing', 'Sonic Racing'],
  ),
  board(
    'Social Media Apps',
    'internet::social_media_apps',
    'Eat only social media apps; leave video streaming services.',
    ['Facebook', 'Instagram', 'TikTok', 'X (Twitter)', 'Snapchat', 'Reddit', 'LinkedIn', 'Pinterest', 'Tumblr', 'Threads', 'Bluesky', 'Mastodon'],
    ['Netflix', 'Hulu', 'Disney+', 'HBO Max', 'Prime Video', 'Apple TV+', 'Peacock', 'Paramount+', 'Crunchyroll', 'BritBox', 'ESPN+', 'Tubi', 'Sling TV', 'Discovery+', 'Pluto TV', 'Mubi', 'Starz', 'Roku Channel'],
  ),
  board(
    'Face Emoji vs Animal Emoji',
    'internet::face_emoji',
    'Eat only face emoji; leave animal emoji.',
    ['😀', '😂', '😍', '😎', '😭', '🤔', '😡', '🥺', '😴', '🥳', '😱', '😉'],
    ['🦋', '🐢', '🐙', '🐝', '🦀', '🐍', '🐌', '🐠', '🦈', '🐬', '🐘', '🦒', '🐊', '🦞', '🐳', '🦂', '🐜', '🦓'],
  ),
  board(
    'Metal Alloys',
    'science::alloys',
    'Eat only metal alloys; leave pure metals.',
    ['Bronze', 'Brass', 'Steel', 'Pewter', 'Sterling Silver', 'Cast Iron', 'Duralumin', 'Nichrome', 'Solder', 'Amalgam', 'White Gold', 'Electrum'],
    ['Copper', 'Tin', 'Zinc', 'Iron', 'Aluminum', 'Nickel', 'Lead', 'Gold', 'Silver', 'Platinum', 'Titanium', 'Chromium', 'Magnesium', 'Cobalt', 'Tungsten', 'Mercury', 'Manganese', 'Cadmium', 'Palladium'],
  ),
  board(
    'AI Chatbot Apps',
    'internet::ai_chatbots',
    'Eat only AI chatbot apps; leave traditional search engines.',
    ['ChatGPT', 'Claude', 'Gemini', 'Copilot', 'Grok', 'Meta AI', 'Character.AI', 'Poe', 'DeepSeek', 'Replika', 'Le Chat', 'Qwen'],
    ['Google', 'Bing', 'Yahoo', 'DuckDuckGo', 'Baidu', 'Yandex', 'Ask.com', 'Ecosia', 'Brave Search', 'Startpage', 'Naver', 'Excite', 'Lycos', 'AltaVista', 'WebCrawler', 'Dogpile', 'Qwant', 'Sogou', 'AOL Search'],
  ),
  board(
    'Video Streaming Services',
    'internet::video_streaming',
    'Eat only video streaming; leave music streaming.',
    ['Netflix', 'Hulu', 'Disney+', 'HBO Max', 'Prime Video', 'Apple TV+', 'Peacock', 'Paramount+', 'Tubi', 'Crunchyroll', 'Pluto TV', 'ESPN+'],
    ['Spotify', 'Apple Music', 'Amazon Music', 'YouTube Music', 'Tidal', 'Deezer', 'Pandora', 'SoundCloud', 'iHeartRadio', 'Audiomack', 'Qobuz', 'Napster', 'Boomplay', 'Anghami', 'JioSaavn', 'Gaana', 'Yandex Music', 'Bandcamp'],
  ),
  board(
    'Romance Languages',
    'language::romance_languages',
    'Eat only Romance languages; leave Germanic languages.',
    ['Spanish', 'French', 'Italian', 'Portuguese', 'Romanian', 'Catalan', 'Galician', 'Occitan', 'Sardinian', 'Romansh', 'Sicilian', 'Corsican'],
    ['English', 'German', 'Dutch', 'Swedish', 'Danish', 'Norwegian', 'Icelandic', 'Afrikaans', 'Yiddish', 'Frisian', 'Luxembourgish', 'Faroese', 'Flemish', 'Swiss German', 'Scots', 'Low German', 'Gothic', 'Norn'],
  ),
  board(
    'The Greek Alphabet',
    'language::greek_alphabet',
    'Eat only Greek letters; leave Latin letters.',
    ['Alpha', 'Beta', 'Gamma', 'Delta', 'Sigma', 'Omega', 'Lambda', 'Theta', 'Phi', 'Pi', 'Epsilon', 'Omicron'],
    ['C', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'Z'],
  ),
  board(
    'Palindromes',
    'language::palindromes',
    'Eat only palindromes; leave non-palindromes.',
    ['level', 'radar', 'civic', 'kayak', 'madam', 'racecar', 'rotor', 'refer', 'noon', 'tenet', 'stats', 'solos'],
    ['lever', 'motor', 'defer', 'moon', 'cynic', 'radio', 'river', 'rover', 'tenant', 'solo', 'kayaks', 'levels', 'sonar', 'denim', 'stressed', 'rotors', 'civics', 'refers'],
  ),
  board(
    'NATO Phonetic Alphabet',
    'language::nato_alphabet',
    'Eat only NATO alphabet words; leave first names.',
    ['Alpha', 'Bravo', 'Foxtrot', 'Golf', 'Hotel', 'Kilo', 'November', 'Quebec', 'Tango', 'Whiskey', 'Zulu', 'Uniform'],
    ['James', 'John', 'Robert', 'William', 'David', 'Richard', 'Joseph', 'Thomas', 'Daniel', 'Matthew', 'Andrew', 'Joshua', 'Christopher', 'George', 'Edward', 'Henry', 'Sarah', 'Emily', 'Jessica'],
  ),
  board(
    'Wild Cats',
    'nature::wild_cats',
    'Eat only wild cat species; leave domestic dog breeds.',
    ['Lion', 'Tiger', 'Leopard', 'Jaguar', 'Cheetah', 'Cougar', 'Lynx', 'Bobcat', 'Ocelot', 'Caracal', 'Serval', 'Snow Leopard'],
    ['Labrador', 'Poodle', 'Beagle', 'Bulldog', 'Boxer', 'Dachshund', 'Rottweiler', 'Chihuahua', 'Pug', 'Husky', 'Corgi', 'Dalmatian', 'Greyhound', 'Mastiff', 'Pomeranian', 'Shih Tzu', 'Doberman', 'Collie', 'Terrier'],
  ),
  board(
    'Snakes vs Lizards',
    'nature::snakes_not_lizards',
    'Eat only snakes; leave lizards.',
    ['Cobra', 'Python', 'Boa', 'Anaconda', 'Rattlesnake', 'Viper', 'Black Mamba', 'Copperhead', 'Cottonmouth', 'Sidewinder', 'Garter Snake', 'Taipan'],
    ['Gecko', 'Iguana', 'Chameleon', 'Komodo Dragon', 'Skink', 'Monitor Lizard', 'Gila Monster', 'Bearded Dragon', 'Anole', 'Basilisk', 'Horned Lizard', 'Tegu', 'Frilled Lizard', 'Chuckwalla', 'Whiptail', 'Agama', 'Glass Lizard', 'Sand Lizard', 'Wall Lizard'],
  ),
  board(
    'Flightless Birds',
    'nature::flightless_birds',
    'Eat only flightless birds; leave birds that can fly.',
    ['Ostrich', 'Emu', 'Penguin', 'Kiwi', 'Cassowary', 'Rhea', 'Kakapo', 'Takahe', 'Weka', 'Dodo', 'Moa', 'Flightless Cormorant'],
    ['Eagle', 'Hawk', 'Falcon', 'Owl', 'Robin', 'Sparrow', 'Pigeon', 'Crow', 'Mallard', 'Goose', 'Swan', 'Pelican', 'Flamingo', 'Peacock', 'Heron', 'Stork', 'Crane', 'Vulture', 'Albatross'],
  ),
  board(
    'Dinosaurs',
    'nature::dinosaurs',
    'Eat only true dinosaurs; leave other prehistoric animals.',
    ['T. Rex', 'Triceratops', 'Stegosaurus', 'Velociraptor', 'Brachiosaurus', 'Allosaurus', 'Ankylosaurus', 'Diplodocus', 'Spinosaurus', 'Iguanodon', 'Parasaurolophus', 'Gallimimus'],
    ['Pterodactyl', 'Pteranodon', 'Quetzalcoatlus', 'Mammoth', 'Mastodon', 'Smilodon', 'Plesiosaur', 'Mosasaurus', 'Ichthyosaur', 'Dimetrodon', 'Megalodon', 'Woolly Rhino', 'Trilobite', 'Ammonite', 'Giant Sloth', 'Glyptodon', 'Liopleurodon', 'Dunkleosteus', 'Titanoboa'],
  ),
  board(
    'Deciduous Trees',
    'nature::deciduous_trees',
    'Eat only deciduous trees; leave evergreen conifers.',
    ['Maple', 'Birch', 'Beech', 'Elm', 'Ash', 'Aspen', 'Willow', 'Hickory', 'Chestnut', 'Sycamore', 'Poplar', 'Walnut'],
    ['Pine', 'Spruce', 'Fir', 'Cedar', 'Hemlock', 'Juniper', 'Yew', 'Sequoia', 'Arborvitae', 'Douglas Fir', 'Blue Spruce', 'White Pine', 'Scots Pine', 'Norway Spruce', 'Balsam Fir', 'Italian Cypress', 'Lodgepole Pine', 'Red Cedar', 'Western Hemlock'],
  ),
  board(
    'Marine Mammals',
    'nature::ocean_mammals',
    'Eat only mammals that live in the ocean; leave fish.',
    ['Blue Whale', 'Humpback Whale', 'Orca', 'Dolphin', 'Porpoise', 'Sperm Whale', 'Beluga Whale', 'Narwhal', 'Sea Lion', 'Walrus', 'Seal', 'Sea Otter'],
    ['Shark', 'Whale Shark', 'Great White', 'Hammerhead', 'Tuna', 'Salmon', 'Cod', 'Mackerel', 'Marlin', 'Swordfish', 'Barracuda', 'Mahi-mahi', 'Grouper', 'Halibut', 'Sardine', 'Herring', 'Stingray', 'Seahorse', 'Eel'],
  ),
  board(
    'Programming Languages',
    'tech::programming_languages',
    'Eat only programming languages; leave markup/data formats.',
    ['Python', 'Java', 'C++', 'Ruby', 'Go', 'Rust', 'Swift', 'Kotlin', 'JavaScript', 'PHP', 'Haskell', 'Lua'],
    ['HTML', 'XML', 'JSON', 'YAML', 'Markdown', 'CSV', 'TOML', 'SVG', 'XHTML', 'INI', 'RSS', 'Atom', 'SGML', 'MathML', 'Protobuf', 'BBCode', 'TSV', 'GeoJSON', 'KML'],
  ),
  board(
    'Apple Products',
    'tech::apple_products',
    'Eat only Apple products; leave Samsung products.',
    ['iPhone', 'iPad', 'MacBook Air', 'MacBook Pro', 'iMac', 'Mac mini', 'Apple Watch', 'AirPods', 'AirPods Max', 'HomePod', 'Apple TV', 'Vision Pro'],
    ['Galaxy S24', 'Galaxy Note', 'Galaxy Tab', 'Galaxy Watch', 'Galaxy Buds', 'Galaxy Fold', 'Galaxy Z Flip', 'Galaxy A54', 'Galaxy Z Fold', 'Galaxy Book', 'Gear S3', 'Galaxy Buds Pro', 'Galaxy S23', 'Galaxy Tab S9', 'Galaxy Buds2', 'The Frame', 'Odyssey G9', 'Galaxy S25', 'Galaxy Ring'],
  ),
  board(
    'Electric Vehicle Makers',
    'tech::ev_makers',
    'Eat only EV makers; leave gas-only car brands.',
    ['Tesla', 'Rivian', 'Lucid', 'Polestar', 'NIO', 'Xpeng', 'BYD', 'VinFast', 'Rimac', 'Fisker', 'Canoo', 'Lordstown'],
    ['Lamborghini', 'McLaren', 'Bugatti', 'Koenigsegg', 'Aston Martin', 'Pagani', 'Ginetta', 'TVR', 'Hennessey', 'Noble', 'Ruf', 'Marcos', 'Radical', 'Donkervoort', 'Spyker', 'Apollo', 'De Tomaso', 'Ascari'],
  ),
  board(
    'Image File Formats',
    'tech::image_file_formats',
    'Eat only image file formats; leave audio file formats.',
    ['JPEG', 'PNG', 'GIF', 'BMP', 'TIFF', 'WebP', 'SVG', 'HEIC', 'ICO', 'AVIF', 'PSD', 'TGA'],
    ['MP3', 'WAV', 'FLAC', 'AAC', 'OGG', 'WMA', 'M4A', 'AIFF', 'ALAC', 'OPUS', 'MIDI', 'AMR', 'APE', 'AU', 'MP2', 'WV', 'CAF', 'PCM', 'DSD'],
  ),
  board(
    'AI Companies',
    'tech::ai_companies',
    'Eat only AI companies; leave social-media companies.',
    ['OpenAI', 'Anthropic', 'DeepMind', 'Cohere', 'Hugging Face', 'Mistral AI', 'Stability AI', 'Midjourney', 'Perplexity', 'Scale AI', 'DeepSeek', 'Character.AI'],
    ['Instagram', 'TikTok', 'Snapchat', 'Pinterest', 'Reddit', 'LinkedIn', 'Tumblr', 'Threads', 'YouTube', 'Twitch', 'BeReal', 'Mastodon', 'Bluesky', 'Vine', 'Myspace', 'Weibo', 'Clubhouse', 'Nextdoor'],
  ),
  board(
    'Impressionist Painters',
    'art::impressionist_painters',
    'Eat only Impressionist painters; leave Renaissance painters.',
    ['Monet', 'Renoir', 'Degas', 'Pissarro', 'Sisley', 'Morisot', 'Caillebotte', 'Cassatt', 'Bazille', 'Guillaumin', 'Childe Hassam', 'Zandomeneghi'],
    ['Leonardo', 'Michelangelo', 'Raphael', 'Titian', 'Botticelli', 'Masaccio', 'Dürer', 'Bellini', 'Mantegna', 'Veronese', 'Tintoretto', 'van Eyck', 'Holbein', 'Fra Angelico', 'Uccello', 'Ghirlandaio', 'Verrocchio', 'Perugino', 'Giorgione'],
  ),
  board(
    'Art Movements',
    'art::art_movements',
    'Eat only art movements; leave architectural styles.',
    ['Impressionism', 'Cubism', 'Surrealism', 'Fauvism', 'Dadaism', 'Pointillism', 'Pop Art', 'Suprematism', 'Abstract Expressionism', 'Symbolism', 'Vorticism', 'Divisionism'],
    ['Brutalism', 'Italianate', 'Georgian', 'Victorian', 'Tudor', 'Palladian', 'Deconstructivism', 'International Style', 'Gothic Revival', 'Greek Revival', 'Beaux-Arts', 'Prairie Style', 'Queen Anne', 'Craftsman', 'Colonial', 'Federal', 'Mission Revival', 'Streamline Moderne', 'Second Empire'],
  ),
  board(
    'Famous Paintings vs Sculptures',
    'art::paintings_not_sculptures',
    'Eat only famous paintings; leave famous sculptures.',
    ['Mona Lisa', 'The Starry Night', 'The Scream', 'Guernica', 'The Last Supper', 'The Night Watch', 'American Gothic', 'Girl w/Pearl Earring', 'The Birth of Venus', 'Las Meninas', 'Nighthawks', 'Sunflowers'],
    ['David', 'The Thinker', 'Venus de Milo', 'The Pieta', 'Statue of Liberty', 'Discobolus', 'Winged Victory', 'Christ the Redeemer', 'Bird in Space', 'The Little Mermaid', 'Manneken Pis', 'Balloon Dog', 'Burghers of Calais', 'Laocoon', 'Moai', 'The Gates of Hell', 'Bust of Nefertiti', 'Apollo Belvedere', 'Charging Bull'],
  ),
  board(
    'Moons of the Solar System',
    'space::solar_system_moons',
    'Eat only moons; leave planets and dwarf planets.',
    ['Europa', 'Io', 'Ganymede', 'Callisto', 'Titan', 'Enceladus', 'Mimas', 'Triton', 'Phobos', 'Deimos', 'Miranda', 'Titania'],
    ['Mercury', 'Venus', 'Earth', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Ceres', 'Eris', 'Haumea', 'Makemake', 'Sedna', 'Quaoar', 'Orcus', 'Gonggong', 'Ixion'],
  ),
  board(
    'Zodiac Constellations',
    'space::zodiac_constellations',
    'Eat only zodiac constellations; leave non-zodiac ones.',
    ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpius', 'Sagittarius', 'Capricornus', 'Aquarius', 'Pisces'],
    ['Orion', 'Ursa Major', 'Ursa Minor', 'Cassiopeia', 'Andromeda', 'Perseus', 'Pegasus', 'Cygnus', 'Lyra', 'Aquila', 'Draco', 'Hercules', 'Centaurus', 'Crux', 'Canis Major', 'Canis Minor', 'Cepheus', 'Hydra', 'Auriga'],
  ),
  board(
    'NASA Space Missions',
    'space::nasa_missions',
    'Eat only NASA space missions; leave SpaceX rockets.',
    ['Apollo 11', 'Apollo 13', 'Voyager 1', 'Voyager 2', 'Curiosity', 'Perseverance', 'New Horizons', 'Juno', 'Cassini', 'Artemis I', 'OSIRIS-REx', 'Viking 1'],
    ['Falcon 1', 'Falcon 9', 'Falcon Heavy', 'Starship', 'Super Heavy', 'Grasshopper', 'Starhopper', 'Dragon', 'Crew Dragon', 'Cargo Dragon', 'BFR', 'Inspiration4', 'Polaris Dawn', 'Merlin', 'Raptor', 'Kestrel', 'SuperDraco', 'Starlink'],
  ),
  board(
    'Space Agencies',
    'space::space_agencies',
    'Eat only space agencies; leave aircraft makers.',
    ['NASA', 'ESA', 'Roscosmos', 'JAXA', 'ISRO', 'CNSA', 'CSA', 'CNES', 'DLR', 'ASI', 'UKSA', 'AEB'],
    ['Boeing', 'Airbus', 'Lockheed Martin', 'Cessna', 'Embraer', 'Bombardier', 'Gulfstream', 'Dassault', 'Northrop Grumman', 'Bell', 'Piper', 'Beechcraft', 'McDonnell Douglas', 'Antonov', 'Tupolev', 'Sukhoi', 'Mikoyan', 'Pilatus', 'Fokker'],
  ),
  board(
    'Stars vs Planets',
    'space::stars_not_planets',
    'Eat only named stars; leave named planets.',
    ['Sirius', 'Betelgeuse', 'Rigel', 'Vega', 'Polaris', 'Arcturus', 'Antares', 'Aldebaran', 'Altair', 'Deneb', 'Procyon', 'Capella'],
    ['Mercury', 'Venus', 'Earth', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Dimidium', 'Janssen', 'Draugr', 'Poltergeist', 'Phobetor', 'Saffar', 'Samh', 'Majriti', 'Tadmor', 'Amateru'],
  ),
  board(
    'Perfect Squares',
    'math::perfect_squares',
    'Eat only perfect squares; leave non-squares.',
    ['4', '9', '16', '25', '36', '49', '64', '81', '100', '121', '144', '196'],
    ['2', '8', '15', '24', '26', '27', '35', '48', '50', '63', '65', '80', '99', '120', '125', '143', '145', '195', '200'],
  ),
  board(
    'Polygons vs Curved Shapes',
    'math::polygons_not_curves',
    'Eat only polygons; leave shapes with curved sides.',
    ['Triangle', 'Square', 'Rectangle', 'Pentagon', 'Hexagon', 'Heptagon', 'Octagon', 'Nonagon', 'Decagon', 'Rhombus', 'Trapezoid', 'Parallelogram'],
    ['Circle', 'Ellipse', 'Oval', 'Semicircle', 'Crescent', 'Heart', 'Disk', 'Sector', 'Ring', 'Teardrop', 'Lens', 'Cardioid', 'Quarter circle', 'Sphere', 'Cone', 'Cylinder', 'Egg shape', 'Torus'],
  ),
  board(
    'Fibonacci Numbers',
    'math::fibonacci_numbers',
    'Eat only Fibonacci numbers; leave non-Fibonacci.',
    ['2', '3', '5', '8', '13', '21', '34', '55', '89', '144', '233', '377'],
    ['4', '6', '7', '9', '10', '11', '12', '14', '15', '22', '33', '35', '56', '88', '90', '100', '143', '145', '234'],
  ),
  board(
    'Multiples of Three',
    'math::multiples_of_three',
    'Eat only multiples of 3; leave non-multiples.',
    ['9', '12', '18', '21', '27', '33', '42', '51', '63', '72', '81', '99'],
    ['10', '11', '14', '16', '20', '22', '25', '28', '32', '35', '40', '44', '49', '50', '55', '64', '70', '88', '100'],
  ),
  board(
    'Norse Mythology Figures',
    'mythology::norse_figures',
    'Eat only Norse myth figures; leave Greek ones.',
    ['Odin', 'Thor', 'Loki', 'Freya', 'Frigg', 'Baldr', 'Heimdall', 'Tyr', 'Njord', 'Freyr', 'Hel', 'Sif'],
    ['Zeus', 'Hera', 'Poseidon', 'Hades', 'Athena', 'Apollo', 'Artemis', 'Ares', 'Aphrodite', 'Hermes', 'Hephaestus', 'Demeter', 'Dionysus', 'Hestia', 'Cronus', 'Persephone', 'Helios', 'Eros', 'Hypnos'],
  ),
  board(
    'Mythical Creatures',
    'mythology::mythical_creatures',
    'Eat only mythical creatures; leave real animals.',
    ['Dragon', 'Unicorn', 'Griffin', 'Phoenix', 'Centaur', 'Minotaur', 'Mermaid', 'Pegasus', 'Kraken', 'Cerberus', 'Werewolf', 'Cyclops'],
    ['Lion', 'Tiger', 'Eagle', 'Horse', 'Shark', 'Octopus', 'Wolf', 'Bear', 'Snake', 'Dolphin', 'Elephant', 'Falcon', 'Owl', 'Goat', 'Bull', 'Crocodile', 'Narwhal', 'Seahorse', 'Komodo dragon'],
  ),
  board(
    'Egyptian Gods',
    'mythology::egyptian_gods',
    'Eat only Egyptian gods; leave Greek gods.',
    ['Ra', 'Osiris', 'Horus', 'Anubis', 'Set', 'Thoth', 'Hathor', 'Bastet', 'Sobek', 'Sekhmet', 'Geb', 'Amun'],
    ['Zeus', 'Hera', 'Poseidon', 'Hades', 'Athena', 'Apollo', 'Artemis', 'Ares', 'Aphrodite', 'Hermes', 'Hephaestus', 'Demeter', 'Dionysus', 'Hestia', 'Cronus', 'Persephone', 'Helios', 'Eros', 'Hypnos'],
  ),
  board(
    'Car Brands',
    'business::car_brands',
    'Eat only car brands; leave motorcycle-only brands.',
    ['Toyota', 'Ford', 'Honda', 'BMW', 'Volkswagen', 'Chevrolet', 'Hyundai', 'Nissan', 'Mercedes-Benz', 'Ferrari', 'Porsche', 'Audi'],
    ['Harley-Davidson', 'Ducati', 'Yamaha', 'Kawasaki', 'Vespa', 'Aprilia', 'Moto Guzzi', 'MV Agusta', 'Husqvarna', 'Norton', 'Benelli', 'Buell', 'Royal Enfield', 'Bimota', 'Cagiva', 'Hyosung', 'Moto Morini', 'Laverda'],
  ),
  board(
    'World Currencies',
    'business::world_currencies',
    'Eat only world currencies; leave country names.',
    ['Euro', 'Yen', 'Dollar', 'Pound', 'Rupee', 'Peso', 'Franc', 'Won', 'Yuan', 'Ruble', 'Dinar', 'Rand'],
    ['Japan', 'France', 'Brazil', 'Canada', 'Mexico', 'India', 'China', 'Germany', 'Egypt', 'Sweden', 'Norway', 'Thailand', 'Vietnam', 'Nigeria', 'Poland', 'Turkey', 'Kenya', 'Peru', 'Chile'],
  ),
  board(
    'Luxury Fashion Brands',
    'business::luxury_brands',
    'Eat only luxury fashion brands; leave fast-fashion brands.',
    ['Gucci', 'Prada', 'Chanel', 'Louis Vuitton', 'Hermes', 'Dior', 'Versace', 'Burberry', 'Fendi', 'Balenciaga', 'Givenchy', 'Valentino'],
    ['Zara', 'H&M', 'Uniqlo', 'Forever 21', 'Primark', 'Shein', 'Gap', 'Old Navy', 'Topshop', 'Mango', 'ASOS', 'Boohoo', 'Pull & Bear', 'Bershka', 'Stradivarius', 'Cotton On', 'New Look', 'Fashion Nova', 'C&A'],
  ),
  board(
    'Cryptocurrencies',
    'business::cryptocurrencies',
    'Eat only cryptocurrencies; leave fiat currencies.',
    ['Bitcoin', 'Ethereum', 'Dogecoin', 'Litecoin', 'Cardano', 'Solana', 'Polkadot', 'Monero', 'XRP', 'Tether', 'Chainlink', 'Avalanche'],
    ['Dollar', 'Euro', 'Yen', 'Pound', 'Rupee', 'Peso', 'Franc', 'Won', 'Yuan', 'Ruble', 'Dinar', 'Rand', 'Lira', 'Real', 'Krona', 'Baht', 'Dirham', 'Shekel', 'Zloty'],
  ),
  board(
    'Airlines vs Hotels',
    'business::airlines_not_hotels',
    'Eat only airlines; leave hotel chains.',
    ['Delta Air Lines', 'United Airlines', 'American Airlines', 'Southwest', 'JetBlue', 'Lufthansa', 'Emirates', 'Qatar Airways', 'Ryanair', 'Air France', 'British Airways', 'Qantas'],
    ['Marriott', 'Hilton', 'Hyatt', 'Sheraton', 'Westin', 'Radisson', 'Ramada', 'Holiday Inn', 'Best Western', 'Four Seasons', 'Ritz-Carlton', 'InterContinental', 'Wyndham', 'Novotel', 'Ibis', 'Comfort Inn', 'DoubleTree', 'Fairmont', 'Sofitel'],
  ),
];

export const SEED_BY_TOPIC: Record<string, BoardContent> = Object.fromEntries(
  SEED_BOARDS.map((b) => [b.topicNormalized, b]),
);

export function getSeedBoard(topicNormalized: string): BoardContent | undefined {
  return SEED_BY_TOPIC[topicNormalized];
}

export const SEED_TOPICS = SEED_BOARDS.map((b) => ({ raw: b.topicRaw, normalized: b.topicNormalized, rule: b.ruleStatement }));
